import { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";
import theme from "@theme/theme";
import { StyledLogo } from "components/NavbarComponent/NavbarComponent.styles";
import { MobileMenuComponent } from "components/Mobile/MenuComponent";
import { UserProfileViewContextProvider } from "context/userProfile/UserProfileViewContextProvider";
import { UserProfileViewContext } from "context/userProfile/UserProfileViewContext";
import { MobileDisplayNameSection } from "components/Mobile/profile/MobileDisplayNameSection";
import { MobileChangeEmailSection } from "components/Mobile/profile/MobileChangeEmailSection";
import { EmailVerifiedSection } from "components/views/profile/EmailVerifiedSection";
import { MobileLogingInSection } from "components/Mobile/profile/MobileLogingInSection";
import { MobileOrganizationInfoSection } from "components/Mobile/profile/MobileOrganizationInfoSection";
import { useAuthContext } from "hooks/useAuthContext";
import { RoutePaths } from "constants/RoutePaths";

const LoadingBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
});

const PageBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  position: "relative",
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(5),
  gap: theme.spacing(2),
  overflow: "hidden",
  height: "80vh",
  backgroundColor: theme.palette.common.white,
}));

const MenuButtonPositioner = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
}));

const StyledTitleTypography = styled(Typography)({
  fontWeight: "bold",
});

const StyledSubtitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  color: theme.palette.text.secondary,
  fontSize: "0.85rem",
}));

const StyledDescriptionTypography = styled(Typography)({
  fontSize: "0.8rem",
});

const StyledYourDataTypography = styled(Typography)({
  fontWeight: "bold",
  fontSize: "1.05rem",
});

const CircularMenuButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const MobileProfilePageContent = () => {
  const { user, getProfilePending, isProfileOfLoggedUser } = useContext(
    UserProfileViewContext,
  );
  const { userState } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("userId");

  useEffect(() => {
    if (userState?.user?.userId && userId && userState.user.userId === userId) {
      navigate(RoutePaths.PROFILE);
    }
  }, [userState?.user?.userId, userId, navigate]);

  if (getProfilePending || !user) {
    return (
      <LoadingBox>
        <CircularProgress />
      </LoadingBox>
    );
  }

  return (
    <PageBox>
      <StyledLogo
        src="ISSVR_Logo.png"
        alt="logo"
        $marginRight={theme.spacing(4)}
      />
      <MenuButtonPositioner>
        <CircularMenuButton onClick={() => setMenuOpen(true)}>
          <MenuIcon />
        </CircularMenuButton>
      </MenuButtonPositioner>
      <MobileMenuComponent open={menuOpen} onClose={() => setMenuOpen(false)} />
      <Stack spacing={2} mt={3}>
        <Box>
          <StyledTitleTypography variant="h5">
            {t("profile.profile")}
          </StyledTitleTypography>
          <StyledSubtitleTypography variant="body1">
            {t("profile.windowDescription")}
          </StyledSubtitleTypography>
        </Box>
        <Box>
          <StyledDescriptionTypography variant="body1">
            {t("profile.displayNameDescription")}
          </StyledDescriptionTypography>
        </Box>
      </Stack>
      <Divider sx={{ mt: -1 }} />
      <Stack gap={2} mt={2}>
        <StyledYourDataTypography>
          {t("profile.yourData")}
        </StyledYourDataTypography>
        <MobileDisplayNameSection
          user={user}
          isProfileOfLoggedUser={isProfileOfLoggedUser}
        />
        <MobileChangeEmailSection email={user.email} />
        <EmailVerifiedSection emailVerified={user.emailConfirmed} />
      </Stack>
      <Divider />
      <MobileLogingInSection user={user} />
      <Divider />
      <MobileOrganizationInfoSection
        organizationName={user.workspace.name}
        patronNames={user.patrons.map((p) => p.displayName)}
        userRole={user.role}
        tagA={user.tagA || ""}
        tagB={user.tagB || ""}
      />
    </PageBox>
  );
};

export const UserProfileViewMobile = () => (
  <UserProfileViewContextProvider>
    <MobileProfilePageContent />
  </UserProfileViewContextProvider>
);
